import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Zap, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import MobileAccordion from '../../components/mobile/MobileAccordion';

const starterFeatures = [
  // Tier 1: Forensic Foundations
  'Step-wise paper evaluation with partial marking',
  'Basic Forensic Report (Metrics, Syllabus & Granular evaluation)',
  'Sections: Paper Summary, Chapter Analysis, Question Analysis',
  'Examiner-style feedback (No hints/solutions)',
  'Personal Progress Tracking Dashboard (Current paper only)',
  'Performance History: None',
  
  // Practice
  '3 AI-generated forensic quizzes per month',
  'Eda AI Coach: Not Available'
];

const coreFeatures = [
  'Everything included in Starter',

  // Tier 2: Active Improvement
  'Advanced Forensic Report (Adds: Trends, Growth & Error Breakdown)',
  'Sections: Progress Tracking, Goal Forecast, Error Breakdown',
  'Overall Confidence Score (Capped at 98%)',
  'Live "Wrong Answer Notebook" for correction tracking',
  'Performance History: Last 7 Days',
  
  // Smart Tools
  'Eda AI Coach: Enabled for report queries and adjustments',
  'Dynamic Weekly Topic Planner (What to study next)',
  
  // Practice
  '12 AI-generated forensic quizzes per month',
  'Standard daily revision system'
];

const proFeatures = [
  'Everything included in Core',

  // Tier 3: Full Forensic Audit
  'Elite Forensic Report (Adds: Targets, Skills & Diagnostics)',
  'Sections: Revision Targets, Skill Analysis, Subject Diagnostics',
  'Root cause identification & Cross-subject skill mapping',
  'Performance History: Full Time (All-time academic data)',
  
  // Elite Tools
  'Priority AI processing for complex answer sheets',
  'Eda AI Coach: Full report modification & planning access',
  'Adaptive Weekly Topic Planner (Auto-changes based on weak areas)',

  // Practice
  'High-volume quiz access (Up to 20 per month)',
  'Elite revision logic with prioritized target chapters'
];

function FeatureList({ features }) {
  return (
    <ul className="space-y-3 mb-8">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <span className="text-sm text-muted-foreground">{feature}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PricingSection() {
  const { login } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');

  return (
    <section 
      id="pricing"
      data-testid="pricing-section"
      className="relative py-20 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Pricing</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Simple, transparent
            <br />
            <span className="text-primary">credit-based pricing</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Pay for what you use. Each paper analysis costs credits based on complexity. No hidden fees.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-2 p-1 rounded-lg bg-muted">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs text-primary">Save 20%</span>
            </button>
          </div>
        </motion.div>

        {/* Pricing Cards - Desktop: Full grid, Mobile: Collapsed */}
        <div className="hidden md:grid md:grid-cols-3 gap-8">
          {/* Starter Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0, duration: 0.5 }}
            data-testid="pricing-starter"
            className="relative rounded-2xl border bg-card border-border p-8"
          >
            <div className="mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Starter</h3>
              <p className="text-sm text-muted-foreground">Perfect for students starting their forensic exam journey</p>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">
                  {billingCycle === 'yearly' ? '₹399' : '₹499'}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-primary font-medium mt-2">1200 credits/month</p>
            </div>

            <FeatureList features={starterFeatures} />

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer" className="block">
              <Button
                className="w-full bg-muted text-foreground hover:bg-muted/80"
                data-testid="pricing-starter-cta"
              >
                Get Started
              </Button>
            </a>
          </motion.div>

          {/* Core Plan - Popular */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.5 }}
            data-testid="pricing-core"
            className="relative rounded-2xl border bg-card border-primary shadow-xl shadow-primary/10 p-8"
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                Most Popular
              </span>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Core</h3>
              <p className="text-sm text-muted-foreground">Engineered for serious students and parents aiming for top board scores</p>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">
                  {billingCycle === 'yearly' ? '₹599' : '₹799'}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-primary font-medium mt-2">3500 credits/month</p>
            </div>

            <FeatureList features={coreFeatures} />

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer" className="block">
              <Button
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="pricing-core-cta"
              >
                Get Started
              </Button>
            </a>
          </motion.div>

          {/* Pro Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            data-testid="pricing-pro"
            className="relative rounded-2xl border bg-card border-border p-8"
          >
            <div className="mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Pro</h3>
              <p className="text-sm text-muted-foreground">Advanced forensic audits for students targeting elite competitive exams</p>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">
                  {billingCycle === 'yearly' ? '₹1,750' : '₹2,199'}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-primary font-medium mt-2">7500 credits/month</p>
            </div>

            <FeatureList features={proFeatures} />

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer" className="block">
              <Button
                className="w-full bg-muted text-foreground hover:bg-muted/80"
                data-testid="pricing-pro-cta"
              >
                Get Started
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Mobile: Same pricing cards as PC with expandable features */}
        <div className="md:hidden space-y-4">
          {/* Starter Plan - Mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-xl border border-border bg-card p-5"
          >
            <div className="mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Starter</h3>
              <p className="text-xs text-muted-foreground">Perfect for students starting their forensic exam journey</p>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">
                  {billingCycle === 'yearly' ? '₹399' : '₹499'}
                </span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>
              <p className="text-xs text-primary font-medium mt-1">1200 credits/month</p>
            </div>

            <MobileAccordion trigger="See features" defaultExpanded={false}>
              <ul className="space-y-2 py-2">
                {starterFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </MobileAccordion>

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer" className="block mt-4">
              <Button className="w-full bg-muted text-foreground hover:bg-muted/80">
                Get Started
              </Button>
            </a>
          </motion.div>

          {/* Core Plan - Mobile (Popular) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="relative rounded-xl border border-primary bg-card p-5 shadow-lg shadow-primary/10"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                Most Popular
              </span>
            </div>

            <div className="mb-4 mt-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Core</h3>
              <p className="text-xs text-muted-foreground">Engineered for serious students and parents aiming for top board scores</p>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">
                  {billingCycle === 'yearly' ? '₹599' : '₹799'}
                </span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>
              <p className="text-xs text-primary font-medium mt-1">3500 credits/month</p>
            </div>

            <MobileAccordion trigger="See features" defaultExpanded={false}>
              <ul className="space-y-2 py-2">
                {coreFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </MobileAccordion>

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer" className="block mt-4">
              <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                Get Started
              </Button>
            </a>
          </motion.div>

          {/* Pro Plan - Mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="rounded-xl border border-border bg-card p-5"
          >
            <div className="mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Pro</h3>
              <p className="text-xs text-muted-foreground">Advanced forensic audits for students targeting elite competitive exams</p>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">
                  {billingCycle === 'yearly' ? '₹1,750' : '₹2,199'}
                </span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>
              <p className="text-xs text-primary font-medium mt-1">7500 credits/month</p>
            </div>

            <MobileAccordion trigger="See features" defaultExpanded={false}>
              <ul className="space-y-2 py-2">
                {proFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </MobileAccordion>

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer" className="block mt-4">
              <Button className="w-full bg-muted text-foreground hover:bg-muted/80">
                Get Started
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Credit Info - Hidden */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-16 text-center hidden"
        >
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Credit usage:</span> 1-5 credits per paper based on subject and length.
            <br />
            All plans include a 7-day free trial. No credit card required.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
